import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiRequest, ApiRequestError } from '../api/client.js';
import styles from './SelectSitePage.module.css';

interface SiteOption {
  cloudId: string;
  name: string;
  url: string;
  avatarUrl: string | null;
}

/**
 * Выбор сайта, когда токен даёт доступ к нескольким инстансам Jira.
 * Список приходит с сервера по идентификатору незавершённого подключения: выбрать можно
 * только то, что действительно было выдано Atlassian.
 */
export function SelectSitePage(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const pendingId = params.get('pending') ?? '';
  const next = params.get('next') ?? '/';
  const [busy, setBusy] = useState<string | null>(null);

  const sites = useQuery({
    queryKey: ['pending-sites', pendingId],
    queryFn: () => apiRequest<{ sites: SiteOption[] }>(`/api/auth/pending/${pendingId}/sites`),
    enabled: pendingId.length > 0,
    retry: false,
  });

  async function choose(cloudId: string): Promise<void> {
    setBusy(cloudId);
    try {
      await apiRequest(`/api/auth/pending/${pendingId}/select`, {
        method: 'POST',
        body: { cloudId },
      });
      void navigate(next, { replace: true });
    } catch {
      setBusy(null);
      // Ссылка подключения живёт минуты — если истекла, начинаем заново.
      void navigate('/connect', { replace: true });
    }
  }

  if (sites.isError) {
    const error = sites.error;
    const code = error instanceof ApiRequestError ? error.code : 'INTERNAL';
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.head}>
            <div className={styles.title}>{t('sites.title')}</div>
            <div className={styles.subtitle}>{t(`errors.${code}`)}</div>
          </div>
          <div className={styles.footer}>
            <button type="button" className={styles.retry} onClick={() => void navigate('/connect')}>
              {t('sites.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const options = sites.data?.sites ?? [];

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.head}>
          <div className={styles.title}>{t('sites.title')}</div>
          <div className={styles.subtitle}>{t('sites.subtitle', { count: options.length })}</div>
        </div>

        <div className={styles.list}>
          {options.map((site) => (
            <button
              type="button"
              key={site.cloudId}
              className={styles.option}
              onClick={() => void choose(site.cloudId)}
              disabled={busy !== null}
            >
              <span className={styles.abbr} aria-hidden="true">
                {site.name.slice(0, 2).toUpperCase()}
              </span>
              <span className={styles.optionText}>
                <span className={styles.optionName}>{site.name}</span>
                <span className={`mono ${styles.optionUrl}`}>{hostOf(site.url)}</span>
              </span>
              {busy === site.cloudId && <span className={styles.busy}>{t('sites.connecting')}</span>}
            </button>
          ))}
        </div>

        <div className={styles.footer}>{t('sites.footer')}</div>
      </div>
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
